import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import {useLocation, useNavigate} from 'react-router-dom';

import {IUser} from '@/app/auth';
import {
  AD_IDS_ITEM,
  USER_COHORT_ITEM,
} from '@/app/main/authentication/pages/SignUp/models/constants';
import {useLoading} from '@/app/zustand/useLoading';
import {featureFlags} from '@/config/featureFlags';
import {LINKS} from '@/constants/LinksConstants';
import {useRemoteFeatureFlags} from '@/dataSource/src/api/firebase/useRemoteFeatureFlags';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';
import logError from '@/utils/errors/logError';

import {
  COnboardingTypeStates,
  DEFAULT_CARD,
} from './constants/OnboardingConstants';
import {getKidData} from './pages/AddKid/services/handleKidInformation';
import {useTrackers} from './trackers';

interface OnboardingContextProps {
  profile: IUser | null;
  isLoadingFeatureFlags: boolean;
}

const OnboardingContext = createContext<OnboardingContextProps | undefined>(
  undefined,
);

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error(
      'useOnboardingContext must be used within OnboardingProvider',
    );
  }
  return context;
};

export const OnboardingViewModelContext: React.FC<{
  children: React.ReactNode;
}> = ({children}) => {
  const {stopLoading} = useLoading();
  const navigate = useNavigate();
  const location = useLocation();
  const {profile, refetchProfile, updateMetadata} = useUsersRepository();
  const kidData = getKidData();
  const selectedTheme =
    profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED || DEFAULT_CARD;
  const {trackViewOnboardingScreen} = useTrackers();
  const {flagsMap, isLoading: isLoadingFeatureFlags} = useRemoteFeatureFlags();

  const handleAdCLicksMetadata = useCallback(async () => {
    const localStorageDataAdIds = localStorage.getItem(AD_IDS_ITEM);
    if (!localStorageDataAdIds) return;

    await updateMetadata({
      AD_CLICK_IDS_V1: localStorageDataAdIds,
    });
  }, [updateMetadata]);

  const previousScreen = useMemo(() => {
    const pathnameParts = location.pathname.split('/').filter(Boolean);
    return pathnameParts[pathnameParts.length - 1] ?? '';
  }, [location.pathname]);

  const handleUserCohortMetadata = useCallback(async () => {
    const localStorageUserCohort = localStorage.getItem(USER_COHORT_ITEM);
    if (!localStorageUserCohort) return;

    await updateMetadata({
      USER_COHORT: localStorageUserCohort,
    });
  }, [updateMetadata]);

  const redirectUserToWaitList = useCallback(async () => {
    window.location.href = LINKS.WAIT_LIST;
  }, []);

  const navigateToDownloadApp = useCallback(() => {
    navigate(
      `/onboarding/download-app?previousScreen=${encodeURIComponent(previousScreen)}`,
    );
  }, [navigate, previousScreen]);

  const isWebViewActive = useMemo(() => {
    return (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    );
  }, []);

  const handleSendWebViewMessage = useCallback(() => {
    stopLoading();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        status: WEB_VIEW_MESSAGES.CONTINUE_ONBOARDING,
        kidUuid: kidData?.uuid,
        choseDesign: selectedTheme,
        previousScreen: 'onboarding viewModelContext completed',
      }),
    );
  }, [kidData?.uuid, selectedTheme, stopLoading]);

  useEffect(() => {
    if (!profile) return;

    if (flagsMap?.isSignUpDisabled) {
      if (!location.pathname.includes('/onboarding/paused')) {
        navigate('/onboarding/paused');
      }
      return;
    }

    switch (profile.onboarding) {
      case COnboardingTypeStates.PENDING:
        if (
          profile.metadata?.ONBOARDING_CARD_THEME_SELECTED ||
          flagsMap?.onboardingV5Enabled
        ) {
          return navigate('/onboarding/kyc');
        } else {
          return navigate('/onboarding/choose-card-theme-category');
        }
        break;
      case COnboardingTypeStates.KYC_APPROVED:
        if (flagsMap?.onboardingV5Enabled) {
          if (isWebViewActive) {
            handleSendWebViewMessage();
          } else {
            // Keep KYC approved screen visible until its local flow finishes.
            if (location.pathname.includes('/onboarding/kyc/approved')) {
              break;
            }
            navigateToDownloadApp();
          }
        } else {
          navigate('/onboarding/add-kid/personal-info');
        }
        break;
      case COnboardingTypeStates.COMPLETED:
        if (isWebViewActive) {
          handleSendWebViewMessage();
          break;
        }

        if (location.pathname.includes('/onboarding/download-app')) break;

        if (featureFlags.isEnabledRequestPhysicalCard) {
          navigate('/onboarding/request-physical-card');
        } else {
          navigateToDownloadApp();
        }
        break;
      default:
        navigateToDownloadApp();
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profile?.onboarding,
    flagsMap?.onboardingV5Enabled,
    flagsMap?.isSignUpDisabled,
    location.pathname,
  ]);

  const tryRefreshProfile = useCallback(async () => {
    try {
      await refetchProfile();
    } catch (error) {
      logError(error, {
        fileName: 'onboarding ViewModelContext',
        functionName: 'tryRefreshProfile',
      });
    }
  }, [refetchProfile]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const cohort = queryParams.get(USER_COHORT_ITEM);
    if (cohort) {
      localStorage.setItem(USER_COHORT_ITEM, cohort);
    }

    if (!profile) return;
    tryRefreshProfile();

    handleAdCLicksMetadata();
    handleUserCohortMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!profile) return;

    const cohortItem = localStorage.getItem(USER_COHORT_ITEM);
    const userHasCohort = cohortItem || profile?.metadata?.USER_COHORT;
    if (featureFlags.isEnabledWaitList && !userHasCohort) {
      redirectUserToWaitList();
      return;
    }
    trackViewOnboardingScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.metadata?.USER_COHORT]);

  return (
    <OnboardingContext.Provider value={{profile, isLoadingFeatureFlags}}>
      {children}
    </OnboardingContext.Provider>
  );
};
